/**
 * Short, human-readable summary of a policy rule's configured value —
 * the number the merchant typed into the Guardrails modal (per-mandate
 * cap, max validity, HITL threshold, etc.). Rendered next to the rule
 * name in the readiness Guardrails drawer so a merchant can tell
 * "r05: Max per-transaction spend cap (per-currency)" apart from the
 * same rule configured at a different cap.
 *
 * Returns `null` for a rule type that has no merchant-tunable
 * parameters (r02, r14, r11, r24, …). The drawer renders `—` in that
 * case. The function is pure and never throws; if the `params` shape
 * is unexpected it falls through to `null`.
 */

import type { Rule } from '@sill/types';

/** Render a per-currency amount map as "USD 500, EUR 400" (sorted by code). */
function currencyMap(caps: Record<string, number> | undefined): string | null {
  if (caps === undefined) return null;
  const entries = Object.entries(caps).filter(([, v]) => Number.isFinite(v));
  if (entries.length === 0) return null;
  entries.sort((a, b) => (a[0] < b[0] ? -1 : 1));
  return entries.map(([code, amt]) => `${code} ${amt.toLocaleString('en-US')}`).join(', ');
}

export function ruleConfiguredValueText(rule: Rule): string | null {
  const t = rule.type;
  // The policy wire shape stores `params` as a discriminated union by
  // `type`. The dashboard reads the two numeric families (r05 spend
  // cap, r07 HITL ceiling) plus the time-window rule (r23). Everything
  // else has no merchant-tunable parameter today.
  const params = rule.params as unknown as Record<string, unknown>;
  if (t === 'r05') {
    const caps = currencyMap(params['caps'] as Record<string, number> | undefined);
    const usd = params['max_amount_usd'];
    const parts: string[] = [];
    if (caps !== null) parts.push(caps);
    if (typeof usd === 'number' && Number.isFinite(usd)) parts.push(`USD ${usd.toLocaleString('en-US')} (legacy)`);
    return parts.length === 0 ? null : `Cap: ${parts.join(' · ')}`;
  }
  if (t === 'r07') {
    const caps = currencyMap(params['auto_approve_caps'] as Record<string, number> | undefined);
    const usd = params['max_auto_approve_usd'];
    const parts: string[] = [];
    if (caps !== null) parts.push(caps);
    if (typeof usd === 'number' && Number.isFinite(usd)) parts.push(`USD ${usd.toLocaleString('en-US')} (legacy)`);
    return parts.length === 0 ? null : `Auto-approve under: ${parts.join(' · ')}`;
  }
  if (t === 'r23') {
    const n = params['max_validity_seconds'];
    if (typeof n === 'number' && Number.isFinite(n)) {
      const mins = Math.round(n / 60);
      return `Max validity: ${mins} min`;
    }
    return null;
  }
  if (t === 'r12') {
    const n = params['max_quantity'];
    if (typeof n === 'number' && Number.isFinite(n)) return `Max quantity: ${n.toLocaleString('en-US')}`;
    return null;
  }
  if (t === 'r29') {
    const n = params['window_seconds'];
    if (typeof n === 'number' && Number.isFinite(n)) {
      const mins = Math.round(n / 60);
      return `Window: ${mins} min`;
    }
    return null;
  }
  return null;
}
